import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../shared/api.js';
import { Avatar } from '../../../layout/Avatar.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { Icon } from '../../../layout/Icon.jsx';
import { useCompanionEntryFee } from '../../../shared/useSettings.js';
import { CardScanInput } from '../components/CardScanInput.jsx';
import { useShiftStatus } from '../../../shared/useShift.js';
import { ShiftDrawerNote } from '../../../shared/ShiftDrawerNote.jsx';
import { useReceptionForm } from '../hooks/useReceptionForm.js';
import { useSubscriptionCoverage } from '../hooks/useSubscriptionCoverage.js';

const METHODS = [
  { key: 'cash', label: 'نقدي' },
  { key: 'card', label: 'بطاقة' },
  { key: 'transfer', label: 'تحويل' }
];

export function Step4CardPayment() {
  const children = useReceptionForm((s) => s.children);
  const updateChild = useReceptionForm((s) => s.updateChild);
  const pricing = useReceptionForm((s) => s.pricing);
  const payment = useReceptionForm((s) => s.payment);
  const setPayment = useReceptionForm((s) => s.setPayment);
  const sharedCard = useReceptionForm((s) => s.sharedCard);
  const setSharedCard = useReceptionForm((s) => s.setSharedCard);

  const companionFee = useCompanionEntryFee();
  // المشتركون شهرياً خارج حساب رسم اللعب — الخادم لا يُفوترهم أصلاً.
  const coverage = useSubscriptionCoverage();
  const me = useAuth((s) => s.user);
  const assignedCashboxId = me?.assignedCashboxId?._id || me?.assignedCashboxId || null;

  const cashboxQuery = useQuery({
    queryKey: ['cashboxes', 'reception'],
    queryFn: async () => {
      const { data } = await api.get('/cashbox', { params: { type: 'reception' } });
      return data?.cashboxes || data?.data || data || [];
    }
  });

  const cashboxes = cashboxQuery.data || [];

  // أثناء الشفت: النقد يدخل درج الشفت حصراً (الخادم يفرضه أيضاً)
  const { shift, cashboxId: shiftBox } = useShiftStatus();
  useEffect(() => {
    if (shiftBox && payment.cashboxId !== shiftBox) setPayment({ cashboxId: shiftBox });
  }, [shiftBox]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (shiftBox || payment.cashboxId || !cashboxes.length) return;
    const preferred = assignedCashboxId
      ? cashboxes.find((cb) => String(cb._id || cb.id) === String(assignedCashboxId))
      : null;
    const pick = preferred || cashboxes[0];
    setPayment({ cashboxId: pick._id || pick.id });
  }, [cashboxes, assignedCashboxId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isHourly = pricing.type === 'hourly';

  const perChildAmount = useMemo(() => {
    if (pricing.type === 'package' && pricing.package) return Number(pricing.package.price) || 0;
    if (pricing.type === 'open') return Number(pricing.openAmount) || 0;
    if (pricing.type === 'fixed') return Number(pricing.fixedAmount) || 0;
    // hourly — billed at exit; show estimated 1-hour rate as guidance only.
    return Number(pricing.rule?.pricePerHour ?? pricing.estimatedAmount ?? 0);
  }, [pricing]);

  const companionsCount = Math.max(0, Math.round(Number(sharedCard.companionsCount) || 0));
  const companionsAmount = companionsCount * companionFee;

  // الأطفال الذين يُحاسَبون فعلاً = غير المشتركين شهرياً.
  const payingCount = children.filter((c) => !coverage.isCovered(c)).length;
  const coveredCount = children.length - payingCount;

  // للتسعير الساعي: رسم اللعب يُحتسب عند الخروج، لكن رسم المرافقين
  // يُحصَّل عند الدخول لأنه لا يعتمد على مدة البقاء.
  const playTotal = isHourly ? 0 : perChildAmount * payingCount;
  const totalAmount = playTotal + companionsAmount;
  const change = Math.max(0, Number(payment.paidAmount || 0) - totalAmount);
  const remaining = Math.max(0, totalAmount - Number(payment.paidAmount || 0));

  return (
    <div>
      <div className="card-h">
        <div className="flex-1">
          <h3 className="text-[14.5px] font-bold">ربط الكارت <span className="text-ink-3 font-normal">(اختياري)</span> والدفع</h3>
          <div className="text-xs text-ink-3 mt-0.5">يمكنك مسح كارت RFID لكل طفل أو ترك الحقل فارغاً ثم أكّد الدفع</div>
        </div>
      </div>
      <div className="card-b space-y-4">
        {/* كارت عائلة واحد لكل الأطفال + المرافقون البالغون */}
        <div className="border border-line rounded-[12px] p-3 bg-[#FFFCF5] space-y-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!sharedCard.enabled}
              onChange={(e) => setSharedCard({ enabled: e.target.checked, status: null })}
            />
            <span className="font-bold text-[13px]">كارت واحد مشترك لكل الأطفال</span>
            <span className="text-[11px] text-ink-3">
              (عائلة على كارت واحد — يمكن إخراج كل طفل على حدة لاحقاً)
            </span>
          </label>

          {sharedCard.enabled ? (
            <div style={{ maxWidth: 420 }}>
              <label className="block text-[11.5px] font-semibold text-ink-2 mb-1">كارت العائلة</label>
              <CardScanInput
                value={sharedCard.uid}
                allowActive
                onChange={(uid) => setSharedCard({ uid, status: null })}
                onVerified={(card) => setSharedCard({ status: card ? 'available' : 'invalid' })}
              />
            </div>
          ) : null}

          <div className="flex items-end gap-3 flex-wrap pt-1 border-t border-line">
            <div>
              <label className="block text-[11.5px] font-semibold text-ink-2 mb-1 mt-2">
                عدد المرافقين (بالغون بدون كارت)
              </label>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSharedCard({ companionsCount: Math.max(0, companionsCount - 1) })}
                  disabled={companionsCount <= 0}
                  aria-label="إنقاص عدد المرافقين"
                >
                  <span className="text-[15px] font-bold leading-none">−</span>
                </button>
                <input
                  type="number"
                  min="0"
                  max="50"
                  className="input text-center"
                  style={{ width: 70 }}
                  value={companionsCount}
                  onChange={(e) =>
                    setSharedCard({
                      companionsCount: Math.min(50, Math.max(0, Math.round(Number(e.target.value) || 0)))
                    })
                  }
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setSharedCard({ companionsCount: Math.min(50, companionsCount + 1) })}
                >
                  <Icon name="plus" size={13} />
                </button>
              </div>
            </div>
            <div className="text-[12px] text-ink-2 pb-2">
              {companionFee > 0 ? (
                <>
                  {formatIQD(companionFee)} × {companionsCount} ={' '}
                  <b className="text-orange-deep">{formatIQD(companionsAmount)}</b>
                </>
              ) : (
                <span className="text-ink-3">دخول المرافقين مجاني (يُضبط الرسم من الإعدادات)</span>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-2" hidden={!!sharedCard.enabled}>
          {children.map((c) => (
            <div
              key={c.tempId}
              className="border border-line rounded-[12px] p-3 grid items-center gap-3"
              style={{ gridTemplateColumns: '42px 1fr 320px' }}
            >
              <Avatar src={c.photoUrl} name={c.name || '?'} id={c.tempId} size="md" />
              <div>
                <div className="font-bold text-[13.5px]">{c.name || '—'}</div>
                <div className="text-[11px] text-ink-3">{c.age || '?'} سنة • {c.gender === 'female' ? 'أنثى' : 'ذكر'}</div>
              </div>
              <CardScanInput
                value={c.cardUid}
                onChange={(uid) => updateChild(c.tempId, { cardUid: uid, cardStatus: null })}
                onVerified={(card) =>
                  updateChild(c.tempId, {
                    cardStatus: card ? 'available' : 'invalid',
                    cardId: card?._id || card?.id || null
                  })
                }
              />
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">طريقة الدفع</label>
            <div className="flex gap-1.5">
              {METHODS.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`btn flex-1 justify-center ${payment.method === m.key ? 'btn-primary' : ''}`}
                  onClick={() => setPayment({ method: m.key })}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <label className="block text-xs font-semibold text-ink-2 mt-3 mb-1">القاصة</label>
            {shift ? (
              <ShiftDrawerNote shift={shift} />
            ) : (
              <select
                className="input"
                value={payment.cashboxId || ''}
                onChange={(e) => setPayment({ cashboxId: e.target.value })}
              >
                <option value="">— اختر القاصة —</option>
                {cashboxes.map((cb) => (
                  <option key={cb._id || cb.id} value={cb._id || cb.id}>
                    {cb.name}
                  </option>
                ))}
              </select>
            )}

            <label className="block text-xs font-semibold text-ink-2 mt-3 mb-1">المبلغ المستلم</label>
            <input
              className="input"
              type="number"
              value={payment.paidAmount}
              onChange={(e) => setPayment({ paidAmount: Number(e.target.value) || 0 })}
              placeholder="0"
            />
          </div>

          <div className="p-3 rounded-[12px] bg-paper-2 space-y-2">
            {isHourly ? (
              <>
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-2">سعر الساعة (تقدير)</span>
                  <b>{formatIQD(perChildAmount)}</b>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-2">عدد الأطفال المحاسَبين</span>
                  <b>{payingCount}</b>
                </div>
                {coveredCount > 0 ? (
                  <div className="flex justify-between text-[13px] text-green">
                    <span>مشتركون شهرياً (بلا رسوم)</span>
                    <b>{coveredCount}</b>
                  </div>
                ) : null}
                {companionsAmount > 0 ? (
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-2">مرافقون ({companionsCount})</span>
                    <b>{formatIQD(companionsAmount)}</b>
                  </div>
                ) : null}
                <div className="border-t border-line pt-2 flex justify-between">
                  <span className="font-bold">الإجمالي عند الدخول</span>
                  <span className="text-[20px] font-extrabold text-orange-deep">{formatIQD(totalAmount)}</span>
                </div>
                <div className="text-[11.5px] text-ink-3 leading-relaxed pt-1 border-t border-line">
                  رسم اللعب الساعي يُحتسب عند الخروج (حسب وقت اللعب){companionsAmount > 0 ? '، أما رسم المرافقين فيُحصَّل الآن' : ''}.
                </div>
              </>
            ) : (
              <>
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-2">للطفل الواحد</span>
                  <b>{formatIQD(perChildAmount)}</b>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-2">عدد الأطفال المحاسَبين</span>
                  <b>{payingCount}</b>
                </div>
                {coveredCount > 0 ? (
                  <div className="flex justify-between text-[13px] text-green">
                    <span>مشتركون شهرياً (بلا رسوم)</span>
                    <b>{coveredCount}</b>
                  </div>
                ) : null}
                {companionsAmount > 0 ? (
                  <div className="flex justify-between text-[13px]">
                    <span className="text-ink-2">مرافقون ({companionsCount})</span>
                    <b>{formatIQD(companionsAmount)}</b>
                  </div>
                ) : null}
                <div className="border-t border-line pt-2 flex justify-between">
                  <span className="font-bold">الإجمالي</span>
                  <span className="text-[20px] font-extrabold text-orange-deep">{formatIQD(totalAmount)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between text-[12.5px]">
              <span className="text-ink-2">المستلم</span>
              <span>{formatIQD(Number(payment.paidAmount) || 0)}</span>
            </div>
            {totalAmount > 0 && remaining > 0 ? (
              <div className="flex justify-between text-[12.5px] text-red">
                <span>المتبقي</span>
                <b>{formatIQD(remaining)}</b>
              </div>
            ) : null}
            {totalAmount > 0 && change > 0 ? (
              <div className="flex justify-between text-[12.5px] text-green">
                <span>الباقي للزبون</span>
                <b>{formatIQD(change)}</b>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
